import useApi from '@/hooks/useApi.ts';
import {IPaginatedData, IPaginatorParams, IQueryPaginatorParams} from '@/types/table';
import {TableUtils} from '@/utils/TableUtils';
import {INotification, INotificationFilter} from '@/types/notification';


interface IUseNotificationsApiHook {
    token?: string
    getNotification: <T = unknown>(id: string) => Promise<INotification<T>>
    toggleRead: (id: string,) => Promise<INotification>
    removeNotification: (id: string) => Promise<void>
    removeNotifications: (ids: string[]) => Promise<void>
    setNotificationsAsRead: (ids: string[]) => Promise<void>
    getNotifications: <T = unknown>(paginator: IPaginatorParams, filter?: INotificationFilter) => Promise<IPaginatedData<INotification<T>>>
    getAllNotifications: <T = unknown>(filter?: INotificationFilter) => Promise<INotification<T>[]>
}

const useNotificationsApi = (): IUseNotificationsApiHook => {
    const {token, apiGet, apiPatch, apiPut} = useApi();
    // const {token, apiGet, apiPatch, apiPut} = useApi('http://localhost:4000');

    const getNotifications = <T = unknown>(paginator: IPaginatorParams, filter?: INotificationFilter): Promise<IPaginatedData<INotification<T>>> => {
        const sort = TableUtils.sort2String<INotification<T>>(paginator.sort)

        return apiGet<IPaginatedData<INotification<T>>, INotificationFilter & IQueryPaginatorParams>('/notifications', {
            ...paginator,
            sort, ...filter
        })
    }

    const getAllNotifications = async <T = unknown>(filter?: INotificationFilter): Promise<INotification<T>[]> => {
        const paginator: IPaginatorParams<INotification<T>> = {
            page: 1,
            limit: 100
        }
        const result: INotification<T>[] = []
        const paginated = await getNotifications<T>(paginator, filter)
        result.push(...paginated.items)
        const promises = await Promise.all(
            Array.from({length: paginated.totalPages}, (_, i) => i + 1)
                .slice(1)
                .map((page) => getNotifications<T>({...paginator, page}, filter))
        )

        return result.concat(...promises.map(({items}) => items).flat())
    }

    const getNotification = <T = unknown>(id: string): Promise<INotification<T>> => {
        return apiGet<INotification<T>>(`/notifications/${id}`)
    }


    const toggleRead = (id: string): Promise<INotification> => {
        return apiPut<undefined, INotification>(`/notifications/${id}`, undefined)
    }

    const removeNotification = async (id: string): Promise<void> => {
        try {
            await apiPatch(`/notifications/${id}`, undefined);
        } catch (error) {
            if (
                typeof error === 'object' &&
                error !== null &&
                'response' in error &&
                (error as { response: { status?: number } }).response?.status === 404
            ) {
                return;
            }
            throw error;
        }
    };

    const removeNotifications = (ids: string[]): Promise<void> => {
        return apiPatch<{ ids: string[] }, void>('/notifications/many/', {ids})
    }

    const setNotificationsAsRead = (ids: string[]): Promise<void> => {
        return apiPut<{ ids: string[] }, void>('/notifications/as-read', {ids})
    }

    return {
        token,
        getNotification,
        toggleRead,
        removeNotification,
        removeNotifications,
        getNotifications,
        setNotificationsAsRead,
        getAllNotifications,
    };
};
export default useNotificationsApi;
